import { WalletConnectContext } from "../../../contexts/WalletConnectContext";
import { useCallback, useContext, useEffect } from 'react';
import { WalletInterface } from "../walletInterface";
import { AccountId, LedgerId, ScheduleSignTransaction, Transaction, SignatureMap, PublicKey } from "@hashgraph/sdk";
import { NetworkName } from "../../../config";
import { SignClientTypes } from "@walletconnect/types";
import { DAppConnector, HederaJsonRpcMethod, HederaSessionEvent, HederaChainId } from "@hashgraph/hedera-wallet-connect";
import EventEmitter from "events";
import { base64ToBytes, bytesToBase64, splitSigAndPub } from "./utils";

export const refreshEvent = new EventEmitter();

const walletConnectProjectId = "5b1c8af356002f599b299b7197701a3f";

const metadata: SignClientTypes.Metadata = {
  name: "Sealcoin schedule transaction helper",
  description: "Sealcoin schedule transaction helper",
  url: window.location.origin,
  icons: [window.location.origin + "/logo192.png"],
};

let network: NetworkName = "mainnet";
let dappConnector: DAppConnector | null = null;
let initPromise: Promise<void> | null = null;

function makeConnector(n: NetworkName) {
  const ledgerId = n === "mainnet" ? LedgerId.MAINNET : LedgerId.TESTNET;
  const chainIds = n === "mainnet" ? [HederaChainId.Mainnet] : [HederaChainId.Testnet];

  return new DAppConnector(
    metadata,
    ledgerId,
    walletConnectProjectId,
    Object.values(HederaJsonRpcMethod),
    [HederaSessionEvent.ChainChanged, HederaSessionEvent.AccountsChanged],
    chainIds
  );
}

export function getDappConnector() {
  if (!dappConnector) {
    dappConnector = makeConnector(network);
  }
  return dappConnector;
}

export async function initializeWalletConnect() {
  const connector = getDappConnector();
  if (!initPromise) initPromise = connector.init();
  await initPromise;
}

export async function openWalletConnectModal() {
  await initializeWalletConnect();
  await getDappConnector().openModal();
  refreshEvent.emit("sync");
}

// Call this when user toggles network:
export async function switchNetwork(next: NetworkName) {
  network = next;

  // disconnect old session if any
  try {
    if (dappConnector) await dappConnector.disconnectAll();
  } catch {}

  // rebuild connector + reset init promise
  dappConnector = makeConnector(network);
  initPromise = null;

  refreshEvent.emit("sync");
}

export function getNetwork(): NetworkName {
  return network;
}
class WalletConnectWallet implements WalletInterface {
  private getSigner() {
    const connector = getDappConnector();
    if (connector.signers.length === 0) {
      throw new Error('No signers found!');
    }
    return connector.signers[0];
  }

  private getAccountId() {
    // Need to convert from walletconnect's AccountId to hashgraph/sdk's AccountId because walletconnect's AccountId and hashgraph/sdk's AccountId are not the same!
    return AccountId.fromString(this.getSigner().getAccountId().toString());
  }

  async signScheduleTransction(scheduleId: string) {
    const signer = this.getSigner();
    const txScheduleSign =  await new ScheduleSignTransaction()
      .setScheduleId(scheduleId)
      .freezeWithSigner(signer);

    const txResult = await txScheduleSign.executeWithSigner(signer);
    return txResult ? txResult.transactionId : null;
  }

  async getSignature(transactionBase64: string) {
    const transactionBytes = base64ToBytes(transactionBase64);
    const signer = this.getSigner();
    const tx = Transaction.fromBytes(transactionBytes);
    const signed = await signer.signTransaction(tx);
    
    const sigMap = await signed.getSignaturesAsync()
    const nodeId = tx.nodeAccountIds![0];
    const txId = tx.transactionId!;
    const nodeSigMap = sigMap.get(nodeId)!;
    const pairSigMap = nodeSigMap.get(txId)!;
    
    const publicKey = Array.from(pairSigMap.keys())[0];
    const publicKeyBytes = publicKey.toBytesDer();
    
    const signature = Array.from(pairSigMap.values())[0];
    
    const out = new Uint8Array(signature.length + publicKeyBytes.length);
    out.set(signature, 0);
    out.set(publicKeyBytes, signature.length);
    
    return bytesToBase64(out);
  }

  async exectueTransaction(transactionBase64: string, signatures: string[], onWaitTick?: (remainingSeconds: number) => void) {
    const signer = this.getSigner();
    const transactionBytes = base64ToBytes(transactionBase64);
    let tx = Transaction.fromBytes(transactionBytes);

    const nodeId = tx.nodeAccountIds![0];
    const txId = tx.transactionId!;

    for (const sig of signatures) {
      const result = splitSigAndPub(base64ToBytes(sig));
      const publicKey = PublicKey.fromBytes(result.publicKey);
      const sigMap = new SignatureMap().addSignature(nodeId, txId, publicKey, result.signature);
      tx = tx.addSignature(publicKey, sigMap);
    }

    const validStartSeconds = txId.validStart!.seconds.toNumber();
    const nowSeconds = Math.floor(Date.now() / 1000);
    const justToBeSafeSeconds = 3;

    let remaining = validStartSeconds - nowSeconds + justToBeSafeSeconds;

    if (onWaitTick) onWaitTick(Math.max(remaining, 0));

    if (remaining > 0) {
      while (remaining > 0) {
        await new Promise((r) => setTimeout(r, 1000));
        remaining -= 1;
        if (onWaitTick) onWaitTick(remaining);
      }
    } else if (remaining < -240) {
      throw new Error("Transaction outdated (validStart too far in the past).");
    }

    const txResult = await tx.executeWithSigner(signer);
    const receipt = await txResult.getReceiptWithSigner(signer);
    return {
      txId: txResult.transactionId.toString(),
      status: receipt.status.toString(),
    };
  }



  disconnect() {
    getDappConnector().disconnectAll().then(() => refreshEvent.emit("sync"));
  }
};
export const walletConnectWallet = new WalletConnectWallet();

// this component will sync the walletconnect state with the context
export const WalletConnectClient = () => {
  // use the HashpackContext to keep track of the hashpack account and connection
  const { setAccountId, setIsConnected } = useContext(WalletConnectContext);

  // sync the walletconnect state with the context
  const syncWithWalletConnectContext = useCallback(() => {
    const accountId = getDappConnector().signers[0]?.getAccountId()?.toString();
    if (accountId) {
      setAccountId(accountId);
      setIsConnected(true);
    } else {
      setAccountId('');
      setIsConnected(false);
    }
  }, [setAccountId, setIsConnected]);

  useEffect(() => {
    // Sync after walletconnect finishes initializing
    refreshEvent.addListener("sync", syncWithWalletConnectContext);

    initializeWalletConnect().then(() => {
      syncWithWalletConnectContext();
    });

    return () => {
      refreshEvent.removeListener("sync", syncWithWalletConnectContext);
    }
  }, [syncWithWalletConnectContext]);
  return null;
};
